import { useEffect, useState, type FormEvent } from 'react';
import { useAdmin } from '../../components/AdminLayout';
import { api, type AuditEntry } from '../../lib/api';
import s from './AdminTenants.module.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TenantRow {
  id: string;
  name: string;
  plan: string;
  billingStatus: string;
  domainCount: number;
  userCount: number;
  reportsPerWeek: number;
  weeklyReportLimit: number;
  maxDomains: number;
  isActive: boolean;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const PLAN_BADGE: Record<string, string> = {
  free: 'badgeInfo',
  pro: 'badgeAccent',
  business: 'badgeSuccess',
  enterprise: 'badgeWarning',
};

const BILLING_BADGE: Record<string, string> = {
  free: 'badgeInfo',
  active: 'badgeSuccess',
  past_due: 'badgeDanger',
  suspended: 'badgeDanger',
};

function badgeClass(map: Record<string, string>, key: string): string {
  return (s as any)[map[key] ?? 'badgeInfo'] ?? s.badgeInfo;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AdminTenants() {
  const { secret } = useAdmin();

  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Detail / edit state
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<TenantRow>>({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [auditEntries, setAuditEntries] = useState<AuditEntry[]>([]);

  // Fetch tenants
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    api.admin
      .tenants(secret)
      .then(({ tenants: data }) => {
        if (!cancelled) {
          // Normalize the API shape – the API may return extra fields
          const rows: TenantRow[] = (data as any[]).map((t) => ({
            id: t.id,
            name: t.name,
            plan: t.plan ?? 'free',
            billingStatus: (t as any).billingStatus ?? (t as any).billing_status ?? 'free',
            domainCount: t.domainCount ?? (t as any).domain_count ?? 0,
            userCount: (t as any).userCount ?? (t as any).user_count ?? 0,
            reportsPerWeek: (t as any).reportsPerWeek ?? (t as any).reports_per_week ?? 0,
            weeklyReportLimit: (t as any).weeklyReportLimit ?? (t as any).weekly_report_limit ?? 1000,
            maxDomains: (t as any).maxDomains ?? (t as any).max_domains ?? 5,
            isActive: (t as any).isActive ?? (t as any).is_active ?? true,
            createdAt: t.createdAt ?? (t as any).created_at ?? '',
          }));
          setTenants(rows);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load tenants');
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [secret]);

  // When a tenant is selected, populate edit form & fetch audit
  useEffect(() => {
    if (!selectedId) return;
    const tenant = tenants.find((t) => t.id === selectedId);
    if (tenant) {
      setEditForm({
        plan: tenant.plan,
        weeklyReportLimit: tenant.weeklyReportLimit,
        maxDomains: tenant.maxDomains,
        billingStatus: tenant.billingStatus,
        isActive: tenant.isActive,
      });
    }
    setSaveMsg(null);

    // Fetch audit entries for this tenant
    api.admin
      .audit(secret, { limit: 20 } as any)
      .then((res) => {
        // Filter client-side if API doesn't support tenant filter
        const entries = (res.data ?? []).filter(
          (entry: any) => entry.tenantId === selectedId || entry.resourceId === selectedId
        );
        setAuditEntries(entries.slice(0, 10));
      })
      .catch(() => setAuditEntries([]));
  }, [selectedId, tenants, secret]);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!selectedId) return;

    setSaving(true);
    setSaveMsg(null);

    try {
      await api.admin.updateTenant(secret, selectedId, editForm as any);
      setSaveMsg('Changes saved successfully.');
      // Update local state
      setTenants((prev) =>
        prev.map((t) => (t.id === selectedId ? { ...t, ...editForm } : t)),
      );
    } catch (err) {
      setSaveMsg(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className={s.loading}>Loading tenants&hellip;</div>;
  }

  if (error) {
    return <div className={s.error}>{error}</div>;
  }

  const selectedTenant = tenants.find((t) => t.id === selectedId);

  return (
    <div className={s.page}>
      <h1 className={s.heading}>Tenants</h1>

      {/* Tenants table */}
      <div className={s.tableWrap}>
        <table className={s.table}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Plan</th>
              <th>Billing</th>
              <th>Domains</th>
              <th>Users</th>
              <th>Reports/Wk</th>
              <th>Limit</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((t) => (
              <tr
                key={t.id}
                className={`${s.clickableRow} ${selectedId === t.id ? s.selectedRow : ''}`}
                onClick={() => setSelectedId(selectedId === t.id ? null : t.id)}
              >
                <td style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{t.name}</td>
                <td>
                  <span className={badgeClass(PLAN_BADGE, t.plan)}>{t.plan}</span>
                </td>
                <td>
                  <span className={badgeClass(BILLING_BADGE, t.billingStatus)}>{t.billingStatus}</span>
                </td>
                <td>{t.domainCount}</td>
                <td>{t.userCount}</td>
                <td>{t.reportsPerWeek.toLocaleString()}</td>
                <td>{t.weeklyReportLimit.toLocaleString()}</td>
                <td>{formatDate(t.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail / edit panel */}
      {selectedTenant && (
        <div className={s.detailPanel}>
          <div className={s.detailHeader}>
            <h2 className={s.detailTitle}>Edit: {selectedTenant.name}</h2>
            <button
              type="button"
              className={s.closeBtn}
              onClick={() => setSelectedId(null)}
              aria-label="Close detail panel"
            >
              &times;
            </button>
          </div>

          {saveMsg && (
            <div className={saveMsg.includes('success') ? s.successMsg : s.error}>
              {saveMsg}
            </div>
          )}

          <form onSubmit={handleSave}>
            <div className={s.formGrid}>
              {/* Plan */}
              <div className={s.field}>
                <label className={s.label}>Plan</label>
                <select
                  className={s.select}
                  value={editForm.plan ?? ''}
                  onChange={(e) => setEditForm((f) => ({ ...f, plan: e.target.value }))}
                >
                  <option value="free">Free</option>
                  <option value="pro">Pro</option>
                  <option value="business">Business</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>

              {/* Weekly report limit */}
              <div className={s.field}>
                <label className={s.label}>Weekly Report Limit</label>
                <input
                  type="number"
                  className={s.input}
                  value={editForm.weeklyReportLimit ?? 0}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      weeklyReportLimit: parseInt(e.target.value, 10) || 0,
                    }))
                  }
                  min={0}
                />
              </div>

              {/* Max domains */}
              <div className={s.field}>
                <label className={s.label}>Max Domains</label>
                <input
                  type="number"
                  className={s.input}
                  value={editForm.maxDomains ?? 0}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      maxDomains: parseInt(e.target.value, 10) || 0,
                    }))
                  }
                  min={0}
                />
              </div>

              {/* Billing status */}
              <div className={s.field}>
                <label className={s.label}>Billing Status</label>
                <select
                  className={s.select}
                  value={editForm.billingStatus ?? ''}
                  onChange={(e) => setEditForm((f) => ({ ...f, billingStatus: e.target.value }))}
                >
                  <option value="free">Free</option>
                  <option value="active">Active</option>
                  <option value="past_due">Past Due</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>

              {/* Active toggle */}
              <div className={s.field}>
                <label className={s.label}>Active</label>
                <div className={s.switchRow}>
                  <button
                    type="button"
                    className={editForm.isActive ? s.switchOn : s.switch}
                    onClick={() => setEditForm((f) => ({ ...f, isActive: !f.isActive }))}
                    aria-label="Toggle active"
                  />
                  <span className={s.switchLabel}>
                    {editForm.isActive ? 'Active' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>

            <div className={s.actions}>
              <button type="submit" className={s.btnPrimary} disabled={saving}>
                {saving ? 'Saving\u2026' : 'Save Changes'}
              </button>
            </div>
          </form>

          {/* Audit trail */}
          {auditEntries.length > 0 && (
            <div className={s.auditSection}>
              <h3 className={s.auditTitle}>Recent Audit Trail</h3>
              {auditEntries.map((entry) => (
                <div key={entry.id} className={s.auditItem}>
                  <span className={s.auditTime}>
                    {new Date(entry.createdAt).toLocaleString()}
                  </span>
                  <span className={s.auditAction}>{entry.action}</span>
                  <span>{entry.userEmail}</span>
                  <span>{entry.resource}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
